import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "bootstrap-icons/font/bootstrap-icons.css";
import { getCampaigns } from "../../apis/campaign";
import AlertBanner from "../AlertBanner";
import { useAuthModal, ONBOARDING_AUTH_MODAL } from "../../context/AuthModalContext";
import { ANALYTICS_CTAS } from "../../analytics/analytics.constants";
import { isCampaignQuotaExhausted } from "../../utils/normalizePlan";
import { isBusinessProfileEmpty } from "../../utils/isBusinessProfileEmpty";
import { normalizeCampaignsResponse } from "../../utils/normalizeCampaign";
import {
  HOME_PAGE_SEO,
  buildDynamicHomeSeo,
} from "../../utils/pageMeta";
import PageHeader, { PageShell } from "../shared/PageHeader";
import GuestHomeBanner from "./GuestHomeBanner";
import NichePicker from "./NichePicker";
import NichePickerModal from "./NichePickerModal";
import OpportunityPreview from "./OpportunityPreview";
import Stats from "./Stats";
import TrendsPreview from "./TrendsPreview";
import { dismissNichePicker, selectShowNichePicker } from "../../store/nicheSlice";
import { getNicheBannerCarouselSlides } from "../../constants/nicheBannerImages";

const NICHE_PICKER_MODAL_DELAY_MS = 10000;
/** Temporarily disabled — keep modal wiring for a later idea. */
const NICHE_PICKER_MODAL_ENABLED = false;

const Dashboard = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const showNichePicker = useSelector(selectShowNichePicker);
  const { openAuthModal } = useAuthModal();
  const { data: planData, loading: planLoading, loaded: planLoaded } = useSelector(
    (state) => state.plan
  );
  const { data: profileData, loading: profileLoading } = useSelector((state) => state.profile);

  const userId = user?.uid;
  const isGuest = !user;
  const [hasCampaigns, setHasCampaigns] = useState(null);
  const [nichePickerModalOpen, setNichePickerModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!userId) {
      setHasCampaigns(null);
      return () => {
        cancelled = true;
      };
    }

    getCampaigns({ limit: 1 })
      .then((data) => {
        if (cancelled) return;
        const { campaigns, total } = normalizeCampaignsResponse(data);
        setHasCampaigns(campaigns.length > 0 || total > 0);
      })
      .catch(() => {
        if (!cancelled) setHasCampaigns(null);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!NICHE_PICKER_MODAL_ENABLED || !isGuest || !showNichePicker) {
      setNichePickerModalOpen(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setNichePickerModalOpen(true);
    }, NICHE_PICKER_MODAL_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [isGuest, showNichePicker]);

  const handleNichePickerDismiss = () => {
    dispatch(dismissNichePicker());
    setNichePickerModalOpen(false);
  };

  const handleNichePickerSelect = () => {
    setNichePickerModalOpen(false);
  };

  const showHomeBanner = isGuest || hasCampaigns === false;
  const bannerSlides = useMemo(() => getNicheBannerCarouselSlides(), []);
  const showProfileAlert =
    Boolean(user) && !profileLoading && isBusinessProfileEmpty(profileData);
  const showNoPlanAlert =
    planLoaded && !planLoading && (!planData || !planData.hasPlans);
  const campaignExhausted = isCampaignQuotaExhausted(planData);
  const [loadedOpportunities, setLoadedOpportunities] = useState([]);
  const homeSeo = useMemo(
    () => buildDynamicHomeSeo(loadedOpportunities),
    [loadedOpportunities]
  );

  return (
    <PageShell className="md:mt-0 px-0 mx-0">
      <PageHeader
        title="Home"
        visible={false}
        metaTitle={HOME_PAGE_SEO.title}
        metaDescription={homeSeo.metaDescription}
        metaKeywords={homeSeo.metaKeywords}
        path={HOME_PAGE_SEO.path}
        imagePath={HOME_PAGE_SEO.imagePath}
        imageAlt={HOME_PAGE_SEO.imageAlt}
        structuredData={homeSeo.structuredData}
        description="Explore important marketing trends and manage your social campaigns in one place."
      />


      {showHomeBanner ? (
        <GuestHomeBanner isDashboard slides={bannerSlides} />
      ) : null}

      {/* NichePicker kept but not rendered inline — shown via delayed modal for guests */}
      {false && isGuest ? <NichePicker /> : null}

      <NichePickerModal
        open={nichePickerModalOpen && isGuest && showNichePicker}
        onDismiss={handleNichePickerDismiss}
        onSelect={handleNichePickerSelect}
      />

      {showProfileAlert ? (
        <AlertBanner
          variant="emphasis"
          iconClass="building"
          title="Complete your business profile"
          action={{
            label: "Set up profile",
            cta: ANALYTICS_CTAS.SETUP_PROFILE,
            onClick: () => openAuthModal(ONBOARDING_AUTH_MODAL),
          }}
        >
          Add your brand/profile name and a few details so create campaigns and posts on your behalf.
        </AlertBanner>
      ) : null}
      {showNoPlanAlert ? (
        <AlertBanner variant="emphasis" iconClass="gem" title="No active plan" action={{ label: "View plans", cta: ANALYTICS_CTAS.VIEW_PLANS, to: "/plans" }}>
          Choose a plan to unlock campaigns and posts for your account.
        </AlertBanner>
      ) : null}
      {campaignExhausted ? (
        <AlertBanner
          variant="emphasis"
          iconClass="megaphone"
          title="You've reached your Plan limit"
          action={{ label: "Upgrade", cta: ANALYTICS_CTAS.UPGRADE_PLAN, to: "/plans" }}
        >
          Your {planData.activePlan?.name ?? "current"} plan includes {planData.campaign?.limit ?? 0}{" "}
          {(planData.campaign?.limit ?? 0) === 1 ? "campaign" : "campaigns"} per billing period — and
          you&apos;ve used all {planData.campaign?.used ?? 0}. Upgrade your plan to create more
          campaigns, or manage the ones you&apos;ve already built.
        </AlertBanner>
      ) : null}
     {!isGuest && <Stats />}
      <OpportunityPreview onOpportunitiesLoaded={setLoadedOpportunities} isDashboard expandWhenSelected />
      {/* <TrendsPreview /> */}

    </PageShell>
  );
};

export default Dashboard;
